import { useCallback, useState } from 'react';

/**
 * Return type for the useConfirmationPopover hook
 */
interface UseConfirmationPopoverReturn {
  /** The current anchor element for the popover */
  anchorEl: HTMLElement | null;
  /** Whether the popover is currently open */
  isOpen: boolean;
  /** Function to open the popover anchored to a specific element */
  openConfirmation: (element: HTMLElement) => void;
  /** Function to close the popover */
  closeConfirmation: () => void;
}

/**
 * Custom hook for managing confirmation popover state in the admin panel.
 *
 * This hook provides a simple API for managing the open/closed state
 * and anchor element of a confirmation popover. It's designed to work
 * seamlessly with the ConfirmationPopover component.
 *
 * @returns Object containing popover state and control functions
 */
export const useConfirmationPopover = (): UseConfirmationPopoverReturn => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openConfirmation = useCallback((element: HTMLElement) => {
    setAnchorEl(element);
  }, []);

  const closeConfirmation = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return {
    anchorEl,
    isOpen: Boolean(anchorEl),
    openConfirmation,
    closeConfirmation,
  };
};
